// Channels: Uber Eats (consent → authorization → store binding → menu
// publication, marketplace pause), the Uber Direct courier, Google Food,
// and the store's outbox ledger — the provider calls the server makes on
// the store's behalf. Also the Uber OAuth landing page: Uber redirects the
// merchant to /integrations/uber-eats/callback?code&state, and the state
// (rid.sid.ts.sig, signed by the server) names the store it belongs to.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { api, ApiError, dateTimeOf, label } from '../../lib/api';
import { useBase } from '../../scope';
import { Check, errMsg, Head, SaveBtn, useAction } from './shared';
import type {
  GoogleFoodSettings, MenuPublishPreview, OutboxJob, UberAuthorizeUrl, UberDirectSettings, UberEatsIntegration,
  UberEatsIntegrationInput, UberStores,
} from './types-b';

function UberEatsPanel(props: { base: string; onWrite: () => void }) {
  const { base, onWrite } = props;
  const [integ, setInteg] = useState<UberEatsIntegration | 'none' | null>(null);
  const [consent, setConsent] = useState<UberAuthorizeUrl | null>(null);
  const [stores, setStores] = useState<UberStores['stores'] | null>(null);
  const [binding, setBinding] = useState<UberEatsIntegrationInput>({ uberStoreId: '', active: true, autoAccept: false });
  const [preview, setPreview] = useState<MenuPublishPreview | null>(null);
  const [published, setPublished] = useState<MenuPublishPreview | null>(null);
  const [paused, setPaused] = useState<boolean | null>(null);
  const connectAct = useAction();
  const bindAct = useAction();
  const menuAct = useAction();
  const pauseAct = useAction();

  const load = useCallback(async () => {
    try {
      const i = await api.get<UberEatsIntegration>(`${base}/uber-eats/integration`);
      setInteg(i);
      setBinding({ uberStoreId: i.uberStoreId, active: i.uberStoreId ? i.active : true, autoAccept: i.autoAccept });
      if (i.connected) setStores((await api.get<UberStores>(`${base}/uber-eats/stores`)).stores);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setInteg('none');
      else connectAct.setError(errMsg(e));
    }
  }, [base, connectAct.setError]);
  useEffect(() => { void load(); }, [load]);

  const connected = integ !== null && integ !== 'none' && integ.connected;
  const bound = connected && integ.uberStoreId !== '';

  return (
    <div className="panel" data-section="uber-eats">
      <h2>Uber Eats</h2>
      {integ === null && <div className="loading">Loading…</div>}
      {integ !== null && (
        <div className="set-stack" style={{ paddingTop: 8, gap: 16 }}>
          <div className="rowline">
            <div className="grow">
              <b data-status={bound ? 'bound' : connected ? 'connected' : 'not_connected'}>
                {bound ? `Bound to ${integ.uberStoreId}` : connected ? 'Connected — no store bound yet' : 'Not connected'}
              </b>
              <span className="sub">
                {connected
                  ? `Merchant account authorized${integ.tokenExpiresAt ? ` · token renews ${dateTimeOf(integ.tokenExpiresAt)}` : ''}`
                  : 'Connect the restaurant’s Uber Eats merchant account to receive marketplace orders.'}
              </span>
            </div>
            {bound && <span className={`chip ${integ.active ? 'chip-paid' : 'chip-unpaid'}`}>{integ.active ? 'active' : 'inactive'}</span>}
            {bound && integ.autoAccept && <span className="chip chip-accepted">auto-accept</span>}
          </div>

          <div>
            <div className="overline" style={{ marginBottom: 6 }}>1 · Authorize</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-outline btn-sm" disabled={connectAct.busy}
                onClick={() => void connectAct.run(async () => { setConsent(await api.get<UberAuthorizeUrl>(`${base}/uber-eats/authorize-url`)); })}>
                {connected ? 'Re-authorize' : 'Connect Uber Eats'}
              </button>
              {consent && (
                <a className="btn btn-primary btn-sm" href={consent.authorizeUrl} data-consent-url>
                  Continue to Uber <ExternalLink size={13} />
                </a>
              )}
            </div>
            <div className="hint" style={{ marginTop: 6 }}>
              Sign in with the merchant account on Uber; Uber sends you back here and the connection completes. The link is good for an hour.
            </div>
            {connectAct.error && <div className="err" role="alert">{connectAct.error}</div>}
          </div>

          {connected && (
            <div>
              <div className="overline" style={{ marginBottom: 6 }}>2 · Bind a marketplace store</div>
              <div className="frow">
                <div className="field">
                  <label htmlFor="uber-store">Uber store</label>
                  <select id="uber-store" className="input" aria-label="Uber store" value={binding.uberStoreId}
                    onChange={(e) => setBinding({ ...binding, uberStoreId: e.target.value })}>
                    <option value="">Choose a store…</option>
                    {stores?.map((s) => <option key={s.storeId} value={s.storeId}>{s.name} ({s.storeId})</option>)}
                    {binding.uberStoreId && !stores?.some((s) => s.storeId === binding.uberStoreId) && (
                      <option value={binding.uberStoreId}>{binding.uberStoreId}</option>
                    )}
                  </select>
                </div>
              </div>
              {stores?.length === 0 && <div className="hint" style={{ marginTop: 4 }}>The merchant account lists no stores.</div>}
              <div style={{ marginTop: 6 }}>
                <Check label="Active (receive orders)" checked={binding.active} onChange={(v) => setBinding({ ...binding, active: v })} />
                <Check label="Auto-accept incoming orders" checked={binding.autoAccept} onChange={(v) => setBinding({ ...binding, autoAccept: v })} />
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                <SaveBtn busy={bindAct.busy} saved={bindAct.saved} disabled={!binding.uberStoreId} label="Save binding"
                  onClick={() => void bindAct.run(async () => {
                    await api.put<UberEatsIntegration>(`${base}/uber-eats/integration`, binding);
                    await load();
                    onWrite();
                  })} />
                <button className="btn btn-danger" disabled={bindAct.busy}
                  onClick={() => {
                    if (!confirm('Disconnect Uber Eats? Marketplace orders stop arriving.')) return;
                    void bindAct.run(async () => {
                      await api.del(`${base}/uber-eats/integration`);
                      setConsent(null); setStores(null); setPreview(null); setPublished(null);
                      await load();
                    });
                  }}>
                  Disconnect
                </button>
              </div>
              {bindAct.error && <div className="err" role="alert">{bindAct.error}</div>}
            </div>
          )}

          {connected && (
            <div data-section="uber-menu">
              <div className="overline" style={{ marginBottom: 6 }}>3 · Menu</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-outline btn-sm" disabled={menuAct.busy}
                  onClick={() => void menuAct.run(async () => { setPreview(await api.get<MenuPublishPreview>(`${base}/uber-eats/menu/preview`)); })}>
                  Preview menu
                </button>
                <button className="btn btn-primary btn-sm" disabled={menuAct.busy || !bound}
                  onClick={() => void menuAct.run(async () => { setPublished(await api.post<MenuPublishPreview>(`${base}/uber-eats/menu/publish`)); onWrite(); })}>
                  Publish to Uber Eats
                </button>
              </div>
              {preview && (
                <div className="kv" style={{ marginTop: 10 }} data-preview>
                  Preview: <b data-stat="items">{preview.itemCount}</b> items, <b>{preview.modifierGroupCount}</b> modifier groups across <b data-stat="categories">{preview.categoryCount}</b> categories
                  {preview.liveItemCount !== null ? <> · the live Uber menu has <b>{preview.liveItemCount}</b> items</> : null}.
                  {preview.problems.length === 0
                    ? <span className="hint"> Ready to publish.</span>
                    : <ul style={{ margin: '6px 0 0 18px', color: 'var(--danger)', fontSize: 13 }}>{preview.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>}
                </div>
              )}
              {published && (
                <div className="kv" style={{ marginTop: 8 }} data-published>
                  Published <b>{published.itemCount}</b> items to Uber Eats.
                </div>
              )}
              {menuAct.error && <div className="err" role="alert">{menuAct.error}</div>}
            </div>
          )}

          {bound && (
            <div>
              <div className="overline" style={{ marginBottom: 6 }}>Marketplace availability</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-outline btn-sm" disabled={pauseAct.busy}
                  onClick={() => void pauseAct.run(async () => { await api.post(`${base}/uber-eats/store/pause`); setPaused(true); })}>
                  Pause on Uber Eats
                </button>
                <button className="btn btn-outline btn-sm" disabled={pauseAct.busy}
                  onClick={() => void pauseAct.run(async () => { await api.post(`${base}/uber-eats/store/resume`); setPaused(false); })}>
                  Resume
                </button>
                {paused !== null && <span className="hint" data-status={paused ? 'paused' : 'open'}>{paused ? 'Paused on Uber Eats.' : 'Open on Uber Eats.'}</span>}
              </div>
              {pauseAct.error && <div className="err" role="alert">{pauseAct.error}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UberDirectPanel(props: { base: string }) {
  const { base } = props;
  const [s, setS] = useState<UberDirectSettings | null>(null);
  const act = useAction();
  useEffect(() => {
    api.get<UberDirectSettings>(`${base}/uber-direct/settings`).then(setS, (e) => act.setError(errMsg(e)));
  }, [base, act.setError]);
  return (
    <div className="panel" data-section="uber-direct">
      <h2>Uber Direct courier</h2>
      {!s && !act.error && <div className="loading">Loading…</div>}
      {s && (
        <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
          <div className="hint">On-demand couriers for this store’s delivery orders, quoted and booked through Uber Direct.</div>
          <Check label="Enabled" checked={s.enabled} onChange={(v) => setS({ ...s, enabled: v })} />
          <div className="field">
            <label htmlFor="uber-customer">Uber customer ID</label>
            <input id="uber-customer" className="input" placeholder="cust-…" aria-label="Uber customer ID" value={s.uberCustomerId ?? ''}
              onChange={(e) => setS({ ...s, uberCustomerId: e.target.value })} />
          </div>
          <div>
            <SaveBtn busy={act.busy} saved={act.saved} label="Save courier settings"
              onClick={() => void act.run(async () => {
                setS(await api.put<UberDirectSettings>(`${base}/uber-direct/settings`, { enabled: s.enabled, uberCustomerId: s.uberCustomerId?.trim() || null }));
              })} />
          </div>
        </div>
      )}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

function GooglePanel(props: { base: string }) {
  const { base } = props;
  const [s, setS] = useState<GoogleFoodSettings | null>(null);
  const act = useAction();
  useEffect(() => {
    api.get<GoogleFoodSettings>(`${base}/google-food/settings`).then(setS, (e) => act.setError(errMsg(e)));
  }, [base, act.setError]);
  return (
    <div className="panel" data-section="google-food">
      <h2>Google Food</h2>
      {!s && !act.error && <div className="loading">Loading…</div>}
      {s && (
        <div className="set-stack" style={{ paddingTop: 8, gap: 12 }}>
          <div className="hint">When on, the store joins the Google food-ordering feed and takes orders placed from Google Search and Maps.</div>
          <Check label="Enabled (include in the Google feed)" checked={s.enabled} onChange={(v) => setS({ enabled: v })} />
          <div>
            <SaveBtn busy={act.busy} saved={act.saved} label="Save Google settings"
              onClick={() => void act.run(async () => { setS(await api.put<GoogleFoodSettings>(`${base}/google-food/settings`, s)); })} />
          </div>
        </div>
      )}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

function OutboxPanel(props: { base: string; tick: number }) {
  const { base, tick } = props;
  const [jobs, setJobs] = useState<OutboxJob[] | null>(null);
  const act = useAction();
  const load = useCallback(() => void act.run(async () => { setJobs(await api.get<OutboxJob[]>(`${base}/outbox`)); }), [base, act.run]);
  useEffect(() => { load(); }, [load, tick]);
  return (
    <div className="panel" data-section="outbox">
      <h2>Provider outbox</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 8px' }}>
        <div className="hint" style={{ flex: 1 }}>Calls the server makes to providers for this store — retried until they land.</div>
        <button className="btn btn-ghost btn-sm" title="Refresh outbox" disabled={act.busy} onClick={load}><RefreshCw size={14} /></button>
      </div>
      {jobs?.length === 0 && <div className="loading">Nothing sent yet.</div>}
      {jobs?.map((j) => (
        <div key={j.jobId} className="rowline" data-job-type={j.jobType} data-status={j.status}>
          <div className="grow">
            <b>{label(j.jobType)}</b>
            <span className="sub">
              {dateTimeOf(j.runAt)} · {j.attempts} attempt{j.attempts === 1 ? '' : 's'}
              {j.detail ? ` · ${j.detail}` : ''}
              {j.lastError ? ` · last error: ${j.lastError}` : ''}
            </span>
          </div>
          <span className={`chip ${j.status === 'completed' ? 'chip-completed' : j.status === 'failed' || j.status === 'dead' ? 'chip-rejected' : 'chip-pending'}`}>{label(j.status)}</span>
        </div>
      ))}
      {act.error && <div className="err" role="alert">{act.error}</div>}
    </div>
  );
}

export function Channels() {
  const base = useBase();
  // a write that rides the outbox nudges the ledger to reload
  const [tick, setTick] = useState(0);
  return (
    <>
      <Head title="Channels" />
      <div className="set-cols">
        <UberEatsPanel base={base} onWrite={() => setTick((t) => t + 1)} />
        <div className="set-stack">
          <UberDirectPanel base={base} />
          <GooglePanel base={base} />
          <OutboxPanel base={base} tick={tick} />
        </div>
      </div>
    </>
  );
}

/** Where Uber sends the merchant back: completes the authorization for the
 * store the signed state names, then returns to that store's Channels. */
export function UberEatsCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const once = useRef(false);
  const code = params.get('code') ?? '';
  const state = params.get('state') ?? '';
  const [rid, sid] = state.split('.');

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    const denied = params.get('error');
    if (denied) { setError(`Uber did not authorize the connection (${params.get('error_description') ?? denied}).`); return; }
    if (!code || !rid || !sid) { setError('This link is missing the authorization code or state. Start again from Settings · Channels.'); return; }
    void (async () => {
      try {
        await api.post<UberEatsIntegration>(`/restaurants/${rid}/stores/${sid}/uber-eats/authorization`, { code, state });
        navigate(`/r/${rid}/s/${sid}/settings/channels`, { replace: true });
      } catch (e) {
        setError(errMsg(e));
      }
    })();
  }, [code, state, rid, sid, params, navigate]);

  return (
    <div style={{ maxWidth: 560, margin: '80px auto', padding: '0 16px' }}>
      <div className="page-head">
        <div>
          <div className="overline">Settings · Channels · Uber Eats</div>
          <h1>Connecting Uber Eats</h1>
        </div>
      </div>
      {!error && <div className="loading">Completing the connection…</div>}
      {error && (
        <>
          <div className="err" role="alert">{error}</div>
          {rid && sid && <Link className="btn btn-outline" style={{ marginTop: 14 }} to={`/r/${rid}/s/${sid}/settings/channels`}>Back to Channels</Link>}
        </>
      )}
    </div>
  );
}
